import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ArrowDownTrayIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline'
import { FilesService, type AttachmentKind, type BrandFileRead } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import Markdown from '../chat/Markdown'
import Spinner from '../chat/Spinner'
import Modal from '../ui/Modal'

export type FileItem = {
  key: string
  name: string
  url: string
  kind: AttachmentKind
  file: BrandFileRead | null
}

const BUTTON =
  'inline-flex h-8 items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-2.5 text-[13px] font-medium text-stone-700 transition-colors hover:bg-stone-50'
const PRIMARY_BUTTON =
  'inline-flex h-8 items-center rounded-lg bg-stone-900 px-3 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40'

type Props = {
  brandId: number
  item: FileItem
  startEditing: boolean
  onSaved: (file: BrandFileRead) => void
  onClose: () => void
}

export default function FileViewer({ brandId, item, startEditing, onSaved, onClose }: Props) {
  const file = item.file
  const textId = file?.editable ? file.id : null
  const [text, setText] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState(startEditing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [discarding, setDiscarding] = useState(false)
  const dirty = text !== null && draft !== text

  useEffect(() => {
    if (!textId) return
    FilesService.readFileContent(textId, brandId)
      .then(({ content }) => {
        setText(content)
        setDraft(content)
      })
      .catch((err: unknown) => setError(apiErrorMessage(err)))
  }, [brandId, textId])

  async function save() {
    if (!textId) return
    setSaving(true)
    try {
      onSaved(await FilesService.saveFileContent(textId, brandId, { content: draft }))
      setText(draft)
      toast.success('Saved')
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  function close() {
    if (dirty) setDiscarding(true)
    else onClose()
  }

  const summary = file?.title ? [file.title, file.description].filter(Boolean).join(' — ') : null

  return (
    <>
      <Modal
        width="max-w-4xl"
        padded={false}
        onClose={close}
        title={
          <span className="min-w-0">
            <span className="block truncate text-base">{item.name}</span>
            {summary && <span className="block truncate text-xs font-normal text-stone-500">{summary}</span>}
          </span>
        }
        headerRight={
          textId && (
            <>
              <button type="button" onClick={() => setEditing(!editing)} className={BUTTON}>
                {editing ? <EyeIcon className="size-4" /> : <PencilIcon className="size-4" />}
                {editing ? 'Preview' : 'Edit'}
              </button>
              <button type="button" disabled={!dirty || saving} onClick={() => void save()} className={PRIMARY_BUTTON}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )
        }
        footer={
          <div className="flex items-center justify-between gap-3">
            <p className="truncate text-xs text-stone-500">
              {file &&
                `${file.mime_type}, ${
                  file.size_bytes < 1_000_000
                    ? `${(file.size_bytes / 1_000).toFixed(1)} KB`
                    : `${(file.size_bytes / 1_000_000).toFixed(1)} MB`
                }`}
            </p>
            <a href={item.url} download={item.name} target="_blank" rel="noopener noreferrer" className={BUTTON}>
              <ArrowDownTrayIcon className="size-4" />
              Download
            </a>
          </div>
        }
      >
        <div className="min-h-0 flex-1 overflow-y-auto bg-stone-50 [scrollbar-width:thin]">
          {textId ? (
            text === null ? (
              <div className="flex h-[60vh] items-center justify-center">
                {error ? (
                  <p className="text-sm text-red-600">{error}</p>
                ) : (
                  <Spinner className="size-4 border-stone-300 border-t-stone-500" />
                )}
              </div>
            ) : editing ? (
              <textarea
                autoFocus
                value={draft}
                spellCheck={false}
                placeholder="Write your note…"
                onChange={(event) => setDraft(event.target.value)}
                className="block h-[60vh] w-full resize-none bg-white p-6 font-mono text-sm leading-relaxed text-stone-900 placeholder:text-stone-400 focus:outline-none"
              />
            ) : (
              <div className="min-h-[60vh] bg-white p-6 text-sm leading-relaxed text-stone-800">
                {draft.trim() ? (
                  <Markdown text={draft} />
                ) : (
                  <p className="text-stone-400">This note is empty. Click Edit to write it.</p>
                )}
              </div>
            )
          ) : item.kind === 'image' ? (
            <div className="flex h-[60vh] items-center justify-center p-6">
              <img src={item.url} alt={item.name} className="max-h-full max-w-full rounded-lg object-contain shadow-sm" />
            </div>
          ) : item.kind === 'video' ? (
            <div className="flex h-[60vh] items-center justify-center bg-stone-900 p-6">
              <video src={item.url} controls className="max-h-full max-w-full" />
            </div>
          ) : item.kind === 'audio' ? (
            <div className="flex h-48 items-center justify-center p-6">
              <audio src={item.url} controls />
            </div>
          ) : file?.mime_type === 'application/pdf' ? (
            <iframe src={item.url} title={item.name} className="block h-[70vh] w-full bg-white" />
          ) : (
            <div className="flex h-48 flex-col items-center justify-center gap-1 p-6 text-center">
              <p className="text-sm font-medium text-stone-700">No preview for this file type</p>
              <p className="text-sm text-stone-500">Download it to open it on your device.</p>
            </div>
          )}
        </div>
      </Modal>

      {discarding && (
        <Modal title="Discard unsaved changes?" width="max-w-sm" onClose={() => setDiscarding(false)}>
          <p className="text-sm text-stone-600">Your edits to {item.name} will be lost.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setDiscarding(false)} className={BUTTON}>
              Keep editing
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 items-center rounded-lg bg-red-600 px-3 text-[13px] font-medium text-white hover:bg-red-700"
            >
              Discard
            </button>
          </div>
        </Modal>
      )}
    </>
  )
}
